import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Animated,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { globalConfigurableData } from '../../../utils/globalConstant';
import { Icon } from '@iconify/react';

// Centralized color configurations
const toastColors = {
  success: {
    bg: globalConfigurableData.profitColor,
    text: globalConfigurableData.cardBgColor,
  },
  destructive: {
    bg: globalConfigurableData.errorTextColor1,
    text: globalConfigurableData.cardBgColor,
  },
  info: {
    bg: globalConfigurableData.primaryTextColor1,
    text: globalConfigurableData.cardBgColor,
  },
  default: {
    bg: globalConfigurableData.secondaryTextColor,
    text: globalConfigurableData.cardBgColor,
  },
};

const Toast = ({
  title,
  description,
  variant = 'default',
  action,
  onClose,
}) => {
  const [animation] = React.useState(new Animated.Value(0));

  React.useEffect(() => {
    Animated.sequence([
      Animated.timing(animation, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.delay(2800),
      Animated.timing(animation, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onClose();
    });
  }, []);

  const getIcon = () => {
    switch (variant) {
      case 'success':
        return 'material-symbols:check-circle-outline';
      case 'destructive':
        return 'material-symbols:error-outline';
      case 'info':
        return 'material-symbols:info-outline';
      default:
        return null;
    }
  };

  const getToastColors = () => toastColors[variant] || toastColors.default;

  return (
    <Animated.View
      style={[
        styles.container,
        {
          backgroundColor: getToastColors().bg,
          opacity: animation,
          transform: [
            {
              translateY: animation.interpolate({
                inputRange: [0, 1],
                outputRange: [-20, 0],
              }),
            },
          ],
        },
      ]}>
      <View style={styles.content}>
        {getIcon() && (
          <View style={styles.iconContainer}>
            <Icon 
              icon={getIcon()} 
              style={{
                color: getToastColors().text,
                width: 24,
                height: 24,
              }}
            />
          </View>
        )}
        <View style={styles.textContainer}>
          {title && (
            <Text 
              style={[
                styles.title,
                { color: getToastColors().text }
              ]}>
              {title}
            </Text>
          )}
          <Text 
            style={[
              styles.description,
              { color: getToastColors().text }
            ]}>
            {description}
          </Text>
        </View>
      </View>
      {action && (
        <TouchableOpacity onPress={action.onPress} style={styles.actionButton}>
          <Text 
            style={[
              styles.actionText,
              { color: getToastColors().text }
            ]}>
            {action.label}
          </Text>
        </TouchableOpacity>
      )}
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    bottom: Platform.OS === 'ios' ? 50 : 30,
    width: '90%',
    maxWidth: 600,
    padding: globalConfigurableData.spacing?.md || 12,
    borderRadius: globalConfigurableData.borderRadius?.md || 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    shadowColor: globalConfigurableData.shadowColor || '#000',
    shadowOffset: {
      width: 0,
      height: -2,
    },
    shadowOpacity: 0.15,
    shadowRadius: 3.84,
    elevation: 5,
    ...(Platform.OS === 'web' && {
      marginBottom: 16,
    }),
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconContainer: {
    marginRight: globalConfigurableData.spacing?.md || 12,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  textContainer: {
    flex: 1,
  },
  title: {
    fontSize: globalConfigurableData.fontSize?.medium || 16,
    fontFamily: globalConfigurableData.fontFamily?.medium,
    marginBottom: globalConfigurableData.spacing?.xs || 2,
  },
  description: {
    fontSize: globalConfigurableData.fontSize?.small || 14,
    fontFamily: globalConfigurableData.fontFamily?.regular,
  },
  actionButton: {
    marginLeft: globalConfigurableData.spacing?.md || 12,
    padding: globalConfigurableData.spacing?.sm || 8,
  },
  actionText: {
    fontSize: globalConfigurableData.fontSize?.small || 14,
    fontFamily: globalConfigurableData.fontFamily?.medium,
  },
});

export default Toast; 